import DialogConfirmCommon from "@/components/common/DialogConfirm";
import AssignDriver from "@/components/common/orders/AssignDriver";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Orders, Vehicle } from "@/interface/entityInterface";
import { useOrderData } from "@/taxi-dispatch/orders/hooks/useOrderData";
import axiosInstance from "@/utils/axiosInstance";
import { endOfDay, format } from "date-fns";
import { Clock, Phone, User } from "lucide-react";
import React, { Dispatch, SetStateAction, useMemo, useState } from "react";
import { useGetList, useRefresh } from "react-admin";
import { toast } from "sonner";

const ScheduledOrders = ({
  setCenter,
}: {
  setCenter: Dispatch<SetStateAction<{ lat: number; lng: number }>>;
}) => {
  const { drivers } = useOrderData();
  const refresh = useRefresh();
  const now = useMemo(() => new Date(), []);
  const { data: orders = [] } = useGetList<Orders>("taxi-orders/scheduled", {
    pagination: { page: 1, perPage: 10 },
    sort: { field: "pickup_time", order: "ASC" },
    filter: {
      status: "NEW_ORDER",
      startPickupTime: now,
      endPickupTime: endOfDay(now)
    },
  });

  const { data: vehicles = [] } = useGetList<Vehicle>("vehicles", {
    pagination: { page: 1, perPage: 5000 },
  });
  const [isCancel, setCancel] = useState(false);
  const [orderId, setOrderId] = useState(0);
  const [loading, setLoading] = useState(false);

  const confirmCancel = async () => {
    setLoading(true);
    try {
      await axiosInstance.put(`/taxi-orders/${orderId}`, {
        status: "CANCELLED",
      });
      toast(`Order ${orderId} cancelled`);
      refresh();
    } catch (err) {
      toast(err.response.data.message);
    } finally {
      setLoading(false);
      setCancel(false);
      setOrderId(0);
    }
  };

  return (
    <div className="grid gap-y-2">
      {orders.length > 0 ? (
        orders.map((item) => (
          <Card key={item.id}>
            <CardContent>
              <div className="flex items-center justify-between mb-2 ">
                <CardTitle>#{item.id}</CardTitle>
                <Button
                  className="ms-4 py-0"
                  variant="destructive"
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setCancel(true);
                    setOrderId(item.id);
                  }}
                >
                  Cancel order
                </Button>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">
                    Pickup:{" "}
                    <span
                      className="text-blue-500 cursor-pointer hover:underline underline-offset-4"
                      onClick={() =>
                        setCenter({
                          lat: item.pickup_latitude,
                          lng: item.pickup_longitude,
                        })
                      }
                    >
                      {item.pickup}
                    </span>{" "}
                    → {item.dropoff || "Not specified"}
                  </p>
                  <div className="flex items-center gap-x-2 text-xs text-gray-500 mt-1 mb-2">
                    <div className="flex gap-x-1 items-center">
                      <Clock className="w-4 h-4" />{" "}
                      {format(item.pickup_time, "HH:mm")}{" "}
                    </div>
                    {item.client_phone && (
                      <div className="flex gap-x-1 items-center">
                        <Phone className="w-4 h-4" /> {item.client_phone}
                      </div>
                    )}
                    {item.client_name && (
                      <div className="flex gap-x-1 items-center">
                        <User className="w-4 h-4" /> {item.client_name}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-center justify-center gap-y-2">
                    <DialogConfirmCommon
                      id={item.id || 0}
                      endpoint="taxi-orders"
                      endpoint2="assign-vehicle"
                      description={`Confirm this action?`}
                      propKey="vehicle_id"
                      dataProp="nickname"
                      dataOptions={vehicles}
                      selectedValue={item.vehicle_id || null}
                    />
                    <AssignDriver order={item} drivers={drivers} />
                  </div>
                </div>
              </div>
            </CardContent>
            <Dialog open={isCancel} onOpenChange={setCancel}>
              <DialogContent className="sm:max-w-[350px]">
                <DialogTitle>Cancel order #{orderId}?</DialogTitle>
                <DialogDescription>
                  Are you sure you want to cancel this order?
                </DialogDescription>
                <div className="flex items-center justify-between">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setCancel(false);
                      setOrderId(0);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button onClick={confirmCancel} disabled={loading}>
                    {loading ? "Confirming..." : "Confirm"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </Card>
        ))
      ) : (
        <p className="text-lg font-semibold">No scheduled orders</p>
      )}
    </div>
  );
};

export default ScheduledOrders;
